import React, { type ReactNode, FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, LinearProgress } from "@mui/material";
import { Model, SurveyModel, RendererFactory, Question, Serializer } from "survey-core";
import { Survey, ReactQuestionFactory } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import {
  selectSurveyInfo,
  setSurveyDataForSpecificSurvey,
  selectAllPreviousSurveyAnswers,
  setSurveyCurrentPage,
  updateSurveyAnswersForAllPreviousSurveys
} from "store/reducers/survey/SurveySlice";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import { getSurveyJson, postSurveyAnswers, type SurveyAnswerPayload } from "apis/v3/surveyApi";
import { rehydrateSurveyWithAnyPreviousAnswers } from "utils/v3/surveyUtils";
import Mixpanel from "utils/mixpanel";
import ChipSelect from "components/v2/Surveys/SurveyComponents/CustomQuestions/ChipSelect";
import LearningStyleOptionGrid from "components/v3/sharedComponents/surveys/CustomQuestions/LearningStyleOptionGrid";
import LoadingAnimation from "components/v3/sharedComponents/LoadingAnimation";
import SurveyWrapperStyles from "./SurveyWrapper.styles";
import SurveyStepper, { type SurveyPage } from "../SurveyStepper";

// Register custom property for radio option grid
Serializer.addProperty("radiogroup", {
  name: "radioOptions",
  type: "itemvalue[]",
  category: "general",
  default: []
});

ReactQuestionFactory.Instance.registerQuestion(
  "gl-chip-select",
  (props) => {
    return React.createElement(ChipSelect, props);
  }
);

ReactQuestionFactory.Instance.registerQuestion(
  "gl-learning-style-option-grid",
  (props) => {
    return React.createElement(LearningStyleOptionGrid, props);
  }
);

RendererFactory.Instance.registerRenderer(
  "checkbox",
  "chip-select",
  "gl-chip-select"
);

RendererFactory.Instance.registerRenderer(
  "radiogroup",
  "learning-style-option-grid",
  "gl-learning-style-option-grid"
);

interface SurveyWrapperProps {
  children?: ReactNode;
  containerClassName?: string;
  surveyId?: string;
  onSurveyInstantiated?: (survey: SurveyModel) => void;
  focusFirstQuestionAutomatic?: boolean;
  onCurrentPageChanging?: (survey: SurveyModel, options: Record<string, any>) => void;
  onComplete?: (surveyModel: SurveyModel) => void;
  onCompleting?: (surveyModel: SurveyModel, options: Record<string, any>) => void;
  surveyJsonOverride?: Record<string, any>;
  overrideNextButtonEnabled?: boolean;
  useStoredSurveyData?: boolean;
  showProgressBar?: boolean;
  showStepper?: boolean;
}

const SurveyWrapper: FC<SurveyWrapperProps> = ({
  children,
  containerClassName = "",
  focusFirstQuestionAutomatic,
  onCurrentPageChanging,
  onComplete,
  onCompleting,
  overrideNextButtonEnabled,
  onSurveyInstantiated,
  surveyId,
  surveyJsonOverride,
  useStoredSurveyData = true,
  showProgressBar = false,
  showStepper = false
}: SurveyWrapperProps) => {
  const classes = SurveyWrapperStyles();
  const [surveyInstance, setSurveyInstance] = useState<SurveyModel | undefined>();
  const [surveyDataIsLoadedFromApi, setSurveyDataIsLoadedFromApi] = useState<boolean>(false);
  const [surveyVersionId, setSurveyVersionId] = useState<string>();
  const [surveyIsRendered, setSurveyIsRendered] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [currentPageProgress, setCurrentPageProgress] = useState<number>(0);
  const [surveyPages, setSurveyPages] = useState<SurveyPage[]>([]);
  const [previousPageNumber, setPreviousPageNumber] = useState<number>(-1);
  const dispatch = useDispatch();
  const allPreviousSurveyAnswers = useSelector(selectAllPreviousSurveyAnswers);

  // Simple scroll to top on page changes
  useEffect(() => {
    if (surveyInstance) {
      const currentPageNumber = surveyInstance.currentPageNo;
      if (previousPageNumber !== -1 && previousPageNumber !== currentPageNumber) {
        window.scrollTo(0, 0);
        // Also try scrolling the survey container
        const surveyContainer = document.querySelector('.sd-container-modern');
        if (surveyContainer) {
          surveyContainer.scrollTop = 0;
        }
      }
      setPreviousPageNumber(currentPageNumber);
    }
  }, [surveyInstance?.currentPageNo]);

  const extractSurveyPages = (surveyModel: SurveyModel): SurveyPage[] => {
    if (!surveyModel || !surveyModel.pages) {
      return [];
    }
    
    return surveyModel.pages.map((page, index) => ({
      name: page.name,
      title: page.title || page.name,
      index: index
    }));
  };

  const calculateCurrentPageProgress = (surveyModel: SurveyModel): number => {
    if (!surveyModel || !surveyModel.currentPage) {
      return 0;
    }

    const currentPage = surveyModel.currentPage;
    
    // Recursively get all visible questions, including those inside panels
    const getAllVisibleQuestions = (elements: any[]): Question[] => {
      const questions: Question[] = [];
      
      for (const element of elements) {
        if (element.getType() === 'panel') {
          // Recursively check questions inside panels
          if (element.isVisible) {
            questions.push(...getAllVisibleQuestions(element.elements));
          }
        } else if (element.getType() !== 'html' && element.isVisible) {
          // Add visible questions (but not HTML elements)
          questions.push(element);
        }
      }
      
      return questions;
    };
    
    const visibleQuestions = getAllVisibleQuestions(currentPage.elements);
    
    if (visibleQuestions.length === 0) {
      return -1; // Use -1 to indicate no questions (hide progress bar)
    }

    // Count answered questions among visible ones
    const answeredQuestions = visibleQuestions.filter((question: Question) => {
      const value = surveyModel.getValue(question.name);
      return value !== null && value !== undefined && value !== "";
    });

    return Math.round((answeredQuestions.length / visibleQuestions.length) * 100);
  };

  const buildSurveyModel = (surveyJson: Record<string, any>): SurveyModel => {
    return new Model({
      ...surveyJson,
      "showProgressBar": "off",
      "showQuestionNumbers": "off",
      "showCompletedPage": false,
      "hideRequiredErrors": false,
      "checkErrorsMode": "onValueChanged",
      "clearInvisibleValues": "onHidden",
      "requiredText": ""
    });
  };

  const possibleStoredSurveys = useSelector(selectSurveyInfo);

  const applyPossibleStoredSurveyData = (survey: SurveyModel, surveyVersionId = ''): void => {
    if (!useStoredSurveyData) {
      return;
    }

    const possibleStoredSurvey = possibleStoredSurveys[surveyVersionId];

    if (possibleStoredSurvey?.data) {
      survey.data = possibleStoredSurvey.data;
      survey.currentPageNo = possibleStoredSurvey.currentPageNumber;
    }
  };

  const setupSurveyInStore = (survey: SurveyModel, surveyVersionId = ''): void => {
    if (onSurveyInstantiated) {
      onSurveyInstantiated(survey);
    }
    setSurveyVersionId(surveyVersionId);
    setSurveyInstance(survey);
    setSurveyDataIsLoadedFromApi(true);
    setSurveyPages(extractSurveyPages(survey));
    dispatch(
      setSurveyDataForSpecificSurvey({
        surveyIdentifier: surveyVersionId,
        surveyData: survey.data
      })
    );
  };

  useEffect(() => {
    if (!surveyDataIsLoadedFromApi && !surveyIsRendered) {
      if (surveyJsonOverride) {
        const survey = buildSurveyModel(surveyJsonOverride);
        applyPossibleStoredSurveyData(survey, surveyId);
        setupSurveyInStore(survey, "overridden");
      } else {
        getSurveyJson(surveyId)
          .then((response: any) => {
            Mixpanel.track(`Survey ${surveyId} started`);
            const surveyJson = { ...(response?.data?.structure || {}) };
            const survey = buildSurveyModel(surveyJson);

            rehydrateSurveyWithAnyPreviousAnswers(
              survey,
              allPreviousSurveyAnswers
            );

            // I'm not sure we want to do this. It means getting the same question on
            //  two different surveys out of sync. I think it's better to lose your changes
            //  to a survey if you bail out, than to have surveys getting out of sync.
            //applyPossibleStoredSurveyData(survey, response.data.surveyId);

            setupSurveyInStore(survey, response.data.surveyId);
          })
          .catch((error) => {
            console.error("error fetching json response", error);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyIsRendered, possibleStoredSurveys]);

  const handleOnCurrentPageChanging = (surveyModel: SurveyModel, options: Record<string, any>): void => {
    if (onCurrentPageChanging) {
      onCurrentPageChanging(surveyModel, options);
    }
  };

  const handleOnCurrentPageChanged = (surveyModel: SurveyModel): void => {
    const currentPageNumber = surveyModel.currentPageNo;
    
    if (surveyVersionId) {
      dispatch(
        setSurveyCurrentPage({
          surveyIdentifier: surveyVersionId,
          currentPageNumber: currentPageNumber
        })
      );
    }
    setNextButtonAvailability(surveyModel);
    setCurrentPageProgress(calculateCurrentPageProgress(surveyModel));
  };

  const handleOnAfterRenderPage = (surveyModel: SurveyModel): void => {
    setNextButtonAvailability(surveyModel);
    setCurrentPageProgress(calculateCurrentPageProgress(surveyModel));
    
    // Initialize slider thumb text for the current page
    setTimeout(() => {
      const sliderQuestions = document.querySelectorAll('.sd-slider');
      sliderQuestions.forEach((sliderElement) => {
        const questionContainer = sliderElement.closest('.sd-question');
        const questionName = questionContainer?.getAttribute('data-name');
        if (questionName) {
          const question = surveyModel.getQuestionByName(questionName);
          if (question && question.getType() === 'slider') {
            const currentValue = surveyModel.getValue(questionName);
            const sliderThumb = sliderElement.querySelector('.sd-slider__thumb');
            if (sliderThumb && currentValue !== undefined) {
              // Get the display text for the current value
              let displayText = currentValue.toString();
              const customLabels = (question as any).customLabels || [];
              if (customLabels.length > 0) {
                const customLabel = customLabels.find((c: any) => c.value === currentValue);
                if (customLabel?.text) {
                  displayText = customLabel.text;
                }
              } else {
                // Fallback to choices if no customLabels
                const choices = question.choices || [];
                const choice = choices.find((c: any) => c.value === currentValue);
                if (choice?.text) {
                  displayText = choice.text;
                }
              }
              sliderThumb.setAttribute('data-value', displayText);
            }
          }
        }
      });
    }, 100);
  };

  const handleOnAfterRenderSurvey = (surveyModel: SurveyModel): void => {
    setNextButtonAvailability(surveyModel);
    setSurveyIsRendered(true);
    setCurrentPageProgress(calculateCurrentPageProgress(surveyModel));
    
    // Ensure pages are set after survey is fully rendered
    setSurveyPages(extractSurveyPages(surveyModel));
  };

  const handleOnValueChanged = (surveyModel: SurveyModel): void => {
    if (surveyVersionId) {
      dispatch(
        setSurveyDataForSpecificSurvey({
          surveyIdentifier: surveyVersionId,
          surveyData: surveyModel.data
        })
      );
    }
    setNextButtonAvailability(surveyModel);
    setCurrentPageProgress(calculateCurrentPageProgress(surveyModel));
    
    // Update slider thumb text with current values
    setTimeout(() => {
      const sliderQuestions = document.querySelectorAll('.sd-slider');
      sliderQuestions.forEach((sliderElement) => {
        const questionContainer = sliderElement.closest('.sd-question');
        const questionName = questionContainer?.getAttribute('data-name');
        if (questionName) {
          const question = surveyModel.getQuestionByName(questionName);
          if (question && question.getType() === 'slider') {
            const currentValue = surveyModel.getValue(questionName);
            const sliderThumb = sliderElement.querySelector('.sd-slider__thumb');
            if (sliderThumb && currentValue !== undefined) {
              // Get the display text for the current value
              let displayText = currentValue.toString();
              const customLabels = (question as any).customLabels || [];
              if (customLabels.length > 0) {
                const customLabel = customLabels.find((c: any) => c.value === currentValue);
                if (customLabel?.text) {
                  displayText = customLabel.text;
                }
              } else {
                // Fallback to choices if no customLabels
                const choices = question.choices || [];
                const choice = choices.find((c: any) => c.value === currentValue);
                if (choice?.text) {
                  displayText = choice.text;
                }
              }
              sliderThumb.setAttribute('data-value', displayText);
            }
          }
        }
      });
    }, 100);
    
    // Auto-scroll to next question after a short delay (skip checkbox questions)
    setTimeout(() => {
      // Find all visible questions on the current page
      const allQuestions = document.querySelectorAll('.sd-question:not([style*="display: none"])');
      let currentQuestionIndex = -1;
      
      // Try multiple approaches to find the current question
      const activeElement = document.activeElement;
      let currentQuestion = null;
      
      if (activeElement) {
        currentQuestion = activeElement.closest('.sd-question');
      }
      
      // If we couldn't find it via active element, try finding the last answered question
      if (!currentQuestion) {
        // Look for questions with values or that have been interacted with
        for (let i = allQuestions.length - 1; i >= 0; i--) {
          const question = allQuestions[i];
          const questionName = question.getAttribute('data-name');
          if (questionName && surveyModel.getValue(questionName) !== undefined) {
            currentQuestion = question;
            break;
          }
        }
      }
      
      if (currentQuestion) {
        currentQuestionIndex = Array.from(allQuestions).indexOf(currentQuestion);
      }
      
      // If we found the current question, scroll to the next one (but skip checkbox questions)
      if (currentQuestionIndex >= 0 && currentQuestionIndex < allQuestions.length - 1) {
        const currentQuestionElement = allQuestions[currentQuestionIndex];
        const nextQuestion = allQuestions[currentQuestionIndex + 1];
        
        // Check if current question is a checkbox - if so, don't auto-scroll
        const isCheckboxQuestion = currentQuestionElement.classList.contains('sd-question--checkbox') || 
                                  currentQuestionElement.querySelector('.sd-checkbox') ||
                                  currentQuestionElement.querySelector('[data-questiontype="checkbox"]');
        
        if (nextQuestion && !isCheckboxQuestion) {
          nextQuestion.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }
    }, 250); // Reduced delay for faster auto-scroll
  };

  const handleOnComplete = (surveyModel: SurveyModel): void => {
    try {
      setSubmitting(true);
      window.scrollTo(0, 0);

      const answeredOn = Math.floor(Date.now() / 1000);
      const formattedPayload = Object.entries(surveyModel.data).map(
        (answer) => {
          const questionName = answer[0];
          const value = answer[1] as SurveyAnswerPayload['value'];
          const question = surveyModel.getQuestionByName(questionName);
          if (!question) {
            throw new Error(`Question ${questionName} not found in survey`);
          }
          // This is so gross, but surveyJS insists on adding "(describe)" to the end of the display value
          //  for any option with the text 'Other' in a radiogroup question. It's deep in the guts of surveyJS.
          // If we use the showOtherItem property, the question wont exist in the survey template when the survey is
          //  instantiated, which breaks other stuff, so that's out too. Sorry mom, you raised me better than this.
          const displayValue = question.getDisplayValue(false) === "Other (describe)" ? "Other" : question.getDisplayValue(false);
          const text = Array.isArray(value) ? displayValue.split(",").map((t: string) => t.trim()) : displayValue;
          const formattedAnswer = {
            name: questionName,
            value,
            text,
            answeredOn
          };
          let questionAsJson = question.toJSON();
          if (["taking-medication"].includes(questionAsJson.name)) {
            questionAsJson = {
              ...questionAsJson,
              choices: ["removed, list was too long"]
            };
          }
          const formattedQuestion = {
            ...questionAsJson,
            type: question.getType(),
            isRequired: question.isRequired,
            isVisible: question.isVisible
          };
          return { ...formattedAnswer, questionAsAsked: formattedQuestion };
        }
      );

      const payload = { surveyId: surveyVersionId, answers: formattedPayload };

      if (surveyJsonOverride) {
        onComplete?.(surveyModel);
      } else {
        postSurveyAnswers(payload)
          .then(() => {
            Mixpanel.track(`Survey ${surveyId} completed`);
            // This needs to get ALL previous answers as soon as we have that endpoint
            getSurveyJson("precision-guidance-survey")
              .then((updatedAnswers) => {
                dispatch(
                  updateSurveyAnswersForAllPreviousSurveys(updatedAnswers)
                );
              })
              .then(() => {
                surveyModel.currentPageNo = 0;
                onComplete?.(surveyModel);
              });
          })
          .catch((error) => {
            Mixpanel.track(`Survey ${surveyId} save to server failed`);
            dispatch(
              openGLSnackbar({
                variant: "error",
                header: "Error saving survey",
                subText:
                  "Sorry, we were unable to save your answers. Please try again, and reach out to us if that doesn't help!"
              })
            );
            window.document.location.reload();
          });
      }
    } catch (e) {
      Mixpanel.track(`Survey ${surveyId} answer parsing failed`, {
        surveyData: surveyModel.data
      });
      dispatch(
        openGLSnackbar({
          variant: "error",
          header: "Error saving survey",
          subText:
            "Sorry, we were unable to save your answers. Please try again, and reach out to us if that doesn't help!"
        })
      );
      window.document.location.reload();
    }
  };

  const setNextButtonAvailability = (surveyModel: SurveyModel): void => {
    if (overrideNextButtonEnabled) {
      // Add a small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const nextButton = document.querySelector<HTMLButtonElement>(".sd-navigation__next-btn");
        const completeButton = document.querySelector<HTMLButtonElement>(
          ".sd-navigation__complete-btn"
        );

        const hasErrors = surveyModel.currentPage.hasErrors(false, false);

        if (nextButton) {
          nextButton.disabled = hasErrors;
        }
        if (completeButton) {
          completeButton.disabled = hasErrors;
        }
      }, 100);
    }
  };

  const handleOnUpdateQuestionCssClasses = (surveyModel: SurveyModel, options: Record<string, any>): void => {
    options.cssClasses.root += ` gl-question-root ${
      options.question.name
    } ${options.question.getType()}`;
  };

  const handleOnAfterRenderQuestion = (surveyModel: SurveyModel, options: { question: Question }): void => {
    // This makes it so that validations run and the next button will be enabled
    //  when the user types something, instead of when they defocus the input, which was super annoying.
    //  If we add it to text fields with a type other that text (like email), it starts validating
    //  the email as they type, which is also annoying.
    // Only handle text inputs that don't have a specific inputType (like email, tel, etc)
    if (options.question.getType() === "text" && options.question.inputType === "text") {
      const questionRoot = document.querySelector(`[data-name="${options.question.name}"]`);
      const input = questionRoot?.querySelector('input');
      
      if (input) {
        input.addEventListener('keydown', (e: KeyboardEvent) => {
          // Small delay to let the input value update
          setTimeout(() => {
            options.question.value = (e.target as HTMLInputElement).value;
            setNextButtonAvailability(surveyModel);
          }, 0);
        });
      }
    }
  };


  return (
    <Grid container className={classes.surveyContainer}>
      <Box className={containerClassName} sx={{ width: "100%" }}>
        {children}
        
        {/* Survey Page Stepper - outside and above the survey container */}
        {showStepper && surveyInstance && !submitting && (
          <SurveyStepper 
            pages={surveyPages} 
            currentPageIndex={surveyInstance.currentPageNo}
            surveyModel={surveyInstance}
          />
        )}
        
        {surveyInstance && !submitting ? (
          <Box sx={{ position: "relative" }} id={surveyId}>
            {/* Current Page Progress Bar - only show if there are questions */}
            {showProgressBar && currentPageProgress >= 0 && (
              <>
                <Box className={classes.pageProgressFade} />
                <Box className={classes.pageProgressContainer}>
                  <LinearProgress
                    variant="determinate"
                    value={currentPageProgress || 2}
                    className={classes.pageProgressBar}
                  />
                </Box>
              </>
            )}
            <Survey
              model={surveyInstance}
              focusFirstQuestionAutomatic={focusFirstQuestionAutomatic}
              onAfterRenderSurvey={handleOnAfterRenderSurvey}
              onCurrentPageChanging={handleOnCurrentPageChanging}
              onCurrentPageChanged={handleOnCurrentPageChanged}
              onAfterRenderPage={handleOnAfterRenderPage}
              onValueChanged={handleOnValueChanged}
              onUpdateQuestionCssClasses={handleOnUpdateQuestionCssClasses}
              onAfterRenderQuestion={handleOnAfterRenderQuestion}
              onComplete={handleOnComplete}
              onCompleting={onCompleting}
            />
          </Box>
        ) : (
          <Grid container className={classes.loadingContainer} sx={{position: "relative"}}>
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)"
              }}
            >
              <LoadingAnimation />
            </Box>
          </Grid>
        )}
      </Box>
    </Grid>
  );
};

export default SurveyWrapper;